import React from 'react'
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react'
import { useUsageControl } from '../hooks/useUsageControl'

interface UsageWarningBannerProps {
  showAlways?: boolean // Show even when not near limit
  onDismiss?: () => void
  dismissible?: boolean
}

export function UsageWarningBanner({
  showAlways = false,
  onDismiss,
  dismissible = true
}: UsageWarningBannerProps) {
  const { stats, getUsageWarning } = useUsageControl()
  const [dismissed, setDismissed] = React.useState(false)

  if (!stats || dismissed) return null

  const warning = getUsageWarning()

  // Don't show if no warning and not forced to always show
  if (!warning && !showAlways) return null

  const handleDismiss = () => {
    setDismissed(true)
    onDismiss?.()
  }

  // Determine styling based on usage level
  const getStyle = () => {
    if (stats.isExceeded) {
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: AlertCircle,
        iconColor: 'text-red-600'
      }
    }

    if (stats.isAtLimit) {
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-800',
        icon: AlertTriangle,
        iconColor: 'text-amber-600'
      }
    }

    if (stats.isNearLimit) {
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: AlertTriangle,
        iconColor: 'text-yellow-600'
      }
    }

    return {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: Info,
      iconColor: 'text-blue-600'
    }
  }

  const style = getStyle()
  const Icon = style.icon

  return (
    <div className={`${style.bg} border ${style.border} rounded-lg p-4 mb-6`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${style.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className={`text-sm font-medium ${style.text}`}>
            {warning || `You've used ${stats.creditsUsed} of ${stats.creditsLimit} credits this month`}
          </div>

          {/* Progress bar */}
          <div className="mt-2 w-full bg-white/50 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                stats.isExceeded
                  ? 'bg-red-500'
                  : stats.isAtLimit
                  ? 'bg-amber-500'
                  : stats.isNearLimit
                  ? 'bg-yellow-500'
                  : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(stats.percentageUsed, 100)}%` }}
            ></div>
          </div>

          <div className={`text-xs ${style.text} mt-1 opacity-75`}>
            {stats.creditsRemaining} credits remaining · {stats.planType} plan
          </div>

          {stats.isExceeded && (
            <div className="mt-3">
              <a
                href="/settings"
                className="inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800 underline"
              >
                Upgrade your plan to continue
              </a>
            </div>
          )}
        </div>

        {dismissible && !stats.isExceeded && (
          <button
            onClick={handleDismiss}
            className={`${style.iconColor} hover:opacity-75 transition-opacity p-1`}
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
